import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CaretRightOutlined,
  PauseOutlined,
  StopTwoTone,
} from "@ant-design/icons";
import styles from "./HeatSlider.module.less";
import { Typography } from "antd";
import { useTheme } from "~/hooks/useTheme";

export interface HeatAndGlobalIndex {
  heatIndex?: number;
  globalIndex: number;
}

interface TypeComponentCardProps {
  heats: string[];
  handleHeatChange: (index?: HeatAndGlobalIndex) => void;
  defaultValue: number;
  isExpanded?: boolean;
}

const HeatSlider = forwardRef(
  (
    {
      heats,
      handleHeatChange,
      defaultValue,
      isExpanded,
    }: TypeComponentCardProps,
    ref,
  ) => {
    const selectedHeatIndex = useRef<number>();

    const { fontSizes } = useTheme();

    const intervalRef = useRef<NodeJS.Timeout>();

    const [sliderIndex, setSliderIndex] = useState<number>();

    const [isPlaying, setIsPlaying] = useState<boolean>(false);

    const isPlayingRef = useRef<boolean>(isPlaying);
    isPlayingRef.current = isPlaying;

    const draw = (index?: number) => {
      if (selectedHeatIndex.current === undefined || heats.length === 0) return;

      const flattedMarkers = markers.flatMap((t) => t.indexedItems);

      selectedHeatIndex.current =
        index === undefined
          ? (selectedHeatIndex.current + 1) % flattedMarkers.length
          : index;

      if (selectedHeatIndex.current === flattedMarkers.length - 1)
        setIsPlaying(false);

      handleHeatChange(
        flattedMarkers[selectedHeatIndex.current].isEnabled
          ? {
              heatIndex: flattedMarkers[selectedHeatIndex.current].heatIndex,
              globalIndex:
                flattedMarkers[selectedHeatIndex.current].globalIndex,
            }
          : {
              heatIndex: undefined,
              globalIndex:
                flattedMarkers[selectedHeatIndex.current].globalIndex,
            },
      );

      setSliderIndex(selectedHeatIndex.current);
    };

    const startTimer = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);

      intervalRef.current = setInterval(() => {
        if (isPlayingRef.current) {
          draw();
        } else {
          if (intervalRef.current) clearInterval(intervalRef.current);

          intervalRef.current = undefined;
        }
      }, 750);
    };

    const markers = useMemo(() => {
      const heatNumbers = heats.map((item) => +item.split(" ")[0]);
      const max = Math.max(...heatNumbers);
      const min =
        heatNumbers[heatNumbers.length - 1] > 5
          ? max - 4
          : Math.min(...heatNumbers);

      const allNumbers = [];
      for (let i = min; i <= max; i++) {
        allNumbers.push(i);
      }
      let globalIndex = 0;
      const indexMap: { [key: number]: number } = {};
      return allNumbers.map((key) => {
        const count = heatNumbers.filter((num) => num === key).length;
        const indexedItems =
          count === 0
            ? [
                {
                  isEnabled: false,
                  globalIndex: globalIndex++,
                  heatIndex: -1,
                },
              ]
            : Array.from({ length: count }, () => {
                if (!indexMap[key]) {
                  indexMap[key] = 0;
                }

                const heatIndex = heatNumbers.findIndex(
                  (num, idx) => num === key && idx >= indexMap[key],
                );

                indexMap[key] = heatIndex + 1;
                return {
                  isEnabled: true,
                  globalIndex: globalIndex++,
                  heatIndex,
                };
              });

        return {
          key,
          indexedItems,
        };
      });
    }, [heats]);

    const calculateHeatIndex = useCallback(
      (index: number) => {
        selectedHeatIndex.current = index;
        const flattedMarkers = markers.flatMap((t) => t.indexedItems);
        const heatIndex = flattedMarkers[index]?.heatIndex;
        return heatIndex < 0 || heatIndex == undefined ? undefined : heatIndex;
      },
      [markers],
    );

    const onClickMarker = useCallback(
      (index: number) => {
        const heatIndex = calculateHeatIndex(index);
        handleHeatChange && handleHeatChange({ heatIndex, globalIndex: index });
        setSliderIndex(index);
        setIsPlaying(false);
      },
      [handleHeatChange, calculateHeatIndex],
    );

    const renderMarkers = useMemo(() => {
      return markers?.map((m, index) => {
        const previousIndexes = markers.findIndex((t) =>
          t.indexedItems.some((t) => t.globalIndex === sliderIndex),
        );
        return (
          <Fragment key={m.key + index}>
            {index > 0 && (
              <div
                className={styles.loadingBar}
                style={{
                  visibility: index > previousIndexes ? "hidden" : "unset",
                }}
              />
            )}
            <div className={styles.flexColumn}>
              <div className={styles.markerContainer}>
                {m.indexedItems.map((indexed) => {
                  const isCurrentSelected = indexed.globalIndex === sliderIndex;
                  const isEnabled = indexed.isEnabled;
                  return isEnabled ? (
                    <div
                      key={m.key + indexed.globalIndex}
                      className={styles.sliderMark}
                      onClick={() => onClickMarker(indexed.globalIndex)}
                      style={{
                        backgroundColor: isCurrentSelected
                          ? "#1677ff"
                          : "white",
                      }}
                    />
                  ) : (
                    <StopTwoTone
                      key={m.key + indexed.globalIndex}
                      className={styles.noDataIcon}
                      onClick={() => onClickMarker(indexed.globalIndex)}
                      twoToneColor={isCurrentSelected ? "red" : ""}
                    />
                  );
                })}
              </div>

              <Typography.Title level={2}>{m.key}</Typography.Title>
            </div>
          </Fragment>
        );
      });
    }, [markers, sliderIndex, onClickMarker]);

    useEffect(() => {
      if (isPlaying) {
        startTimer();
      } else {
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlaying]);

    useEffect(() => {
      if (
        isPlayingRef.current === false &&
        selectedHeatIndex.current === undefined &&
        markers.length > 0
      ) {
        const markerIndex = markers
          .flatMap((t) => t.indexedItems)
          .findIndex((t) => t.heatIndex === defaultValue);
        selectedHeatIndex.current = markerIndex;
        draw(selectedHeatIndex.current);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue, markers]);

    useEffect(() => {
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        onClickPrevious: () => {
          const previousIndex =
            sliderIndex != undefined ? sliderIndex - 1 : undefined;
          if (previousIndex != undefined && previousIndex >= 0) {
            onClickMarker(previousIndex!);
          }
        },
        onClickNext: () => {
          const nextIndex =
            sliderIndex != undefined ? sliderIndex + 1 : undefined;

          if (
            nextIndex != undefined &&
            nextIndex <= markers.flatMap((t) => t.indexedItems).length - 1
          ) {
            onClickMarker(nextIndex!);
          }
        },
      }),
      [onClickMarker, sliderIndex, markers],
    );

    return (
      heats?.length > 1 && (
        <div
          className={isExpanded ? styles.heatSliderExpanded : styles.HeatSlider}
        >
          <div className={styles.iconWrapper}>
            {isPlaying ? (
              <PauseOutlined
                style={{ fontSize: fontSizes.iconSize }}
                onClick={() => {
                  setIsPlaying(false);
                }}
              />
            ) : (
              <CaretRightOutlined
                style={{ fontSize: fontSizes.iconSize }}
                onClick={() => {
                  setIsPlaying(true);
                }}
              />
            )}
            <Typography.Title level={2}>Heat</Typography.Title>
          </div>

          <div className={styles.sliderWrapper}>
            <div className={styles.sliderContainer}>{renderMarkers}</div>
          </div>
        </div>
      )
    );
  },
);

export default HeatSlider;
